// API service functions for authenticated requests
import { getAccessToken, refreshAccessToken } from './authService.ts';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Core API Response interface
export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  status: number;
}

// Types for story management
export interface Character {
  id: number;
  name: string;
  description: string;
  image_url: string | null;
  status: 'generating' | 'completed' | 'pending';
  order_index: number;
  character_type?: string;
  created_at?: string;
}

export interface StoryWithCharacters {
  story_id: number;
  story_title: string;
  created_at: string | null;
  characters: Character[];
}

export interface SidebarStory {
  id: number;
  story_name: string;
  child_name: string;
  status?: string;
  created_at: string;
}

export interface Scene {
  id: number;
  scene_number: number;
  description: string;
  image_prompt: string;
  image_url: string | null;
  status: 'generating' | 'completed';
}

export interface StoryResponse {
  id: number;
  title: string;
  status: string;
  current_step: string;
  character_phase: string;
  has_final_video: boolean;
  final_url: string | null;
  characters: Character[];
  scenes: Scene[];
  created_at: string;
  updated_at: string;
}

export interface StorySummary {
  id: number;
  child_name: string;
  story_name: string;
  status: string;
  created_at: string;
}

export interface CharacterRegenerateResponse {
  success: boolean;
  message: string;
}

export interface PresignedUploadResponse {
  upload_url: string;
  key: string;
  file_url: string;
}

// Request deduplication cache
const requestCache = new Map<string, Promise<ApiResponse<any>>>();

// In-flight requests map for true de-duplication
const inFlightRequests = new Map<string, Promise<ApiResponse<any>>>();

// Function to clear request cache for a specific endpoint
export const clearRequestCache = (endpoint: string) => {
  // Clear both maps for safety
  const matches = (key: string) => key.startsWith(endpoint);
  Array.from(requestCache.keys()).filter(matches).forEach(k => requestCache.delete(k));
  Array.from(inFlightRequests.keys()).filter(matches).forEach(k => inFlightRequests.delete(k));
  console.log('API Cache: Cleared for', endpoint);
};

// Make authenticated API request with automatic token refresh
export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {},
  force: boolean = false
): Promise<ApiResponse<T>> => {
  const method = (options.method || 'GET').toUpperCase();
  const token = getAccessToken();
  
  // Create cache key from endpoint and options
  const cacheKey = `${endpoint}:${JSON.stringify(options)}`;
  
  // 1) De-dupe only while in flight (all methods)
  if (!force && inFlightRequests.has(cacheKey)) {
    console.log('API Request De-duplication: Reusing in-flight request for', endpoint);
    return inFlightRequests.get(cacheKey)!;
  }
  
  // 2) Optional persistent cache ONLY for non-GETs
  if (!force && method !== 'GET' && requestCache.has(cacheKey)) {
    console.log('API Request Cache: Reusing cached request for', endpoint);
    return requestCache.get(cacheKey)!;
  }
  
  console.log('API Request Debug:', { endpoint, method, token: token ? 'exists' : 'missing', fullUrl: `${API_BASE_URL}${endpoint}` });
  
  // Build headers: don't set Content-Type for GET (avoids needless preflights)
  const hasBody = !!(options as any).body;
  const defaultHeaders: HeadersInit = {};
  if (hasBody) defaultHeaders['Content-Type'] = 'application/json';
  if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;

  const config: RequestInit = {
    ...options,
    headers: { ...defaultHeaders, ...options.headers },
    // Only include credentials if you actually need cookies
    // credentials: 'include',
  };

  // Create the request promise
  const promise = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, config);
      const ct = res.headers.get('content-type') || '';
      const body = ct.includes('application/json') ? await res.json() : await res.text();

      // Handle 401 Unauthorized - try to refresh token and retry
      if (res.status === 401 && token) {
        console.log('API: 401 received, attempting token refresh...');
        const refreshSuccess = await refreshAccessToken();
        
        if (refreshSuccess) {
          console.log('API: Token refresh successful, retrying request...');
          // Get new token and retry the request
          const newToken = getAccessToken();
          if (newToken) {
            const retryConfig: RequestInit = {
              ...config,
              headers: {
                ...config.headers,
                'Authorization': `Bearer ${newToken}`,
              },
            };
            
            const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, retryConfig);
            const retryCt = retryResponse.headers.get('content-type') || '';
            const retryBody = retryCt.includes('application/json') ? await retryResponse.json() : await retryResponse.text();

            if (!retryResponse.ok) {
              return {
                error: retryBody.detail || retryBody.message || `HTTP ${retryResponse.status}`,
                status: retryResponse.status,
              };
            }

            return {
              data: retryBody,
              status: retryResponse.status,
            };
          }
        } else {
          console.log('API: Token refresh failed, user will be logged out');
          return {
            error: 'Unauthorized - token refresh failed',
            status: res.status,
          };
        }
      }

      if (!res.ok) {
        return {
          error: body.detail || body.message || `HTTP ${res.status}`,
          status: res.status,
        };
      }

      return {
        data: body,
        status: res.status,
      };
    } catch (e) {
      return {
        error: e instanceof Error ? e.message : 'Network error',
        status: 0,
      };
    } finally {
      // Remove from in-flight when request completes
      inFlightRequests.delete(cacheKey);
    }
  })();

  // Store only while in flight (all methods)
  inFlightRequests.set(cacheKey, promise);

  // Persist ONLY for non-GETs if desired
  if (method !== 'GET') {
    requestCache.set(cacheKey, promise);
  }

  return promise;
};

// Convenience methods
export const apiGet = <T = any>(endpoint: string, force: boolean = false): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, { method: 'GET' }, force);
};

export const apiPost = <T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
};

export const apiPut = <T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
};

export const apiDelete = <T = any>(endpoint: string): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, { method: 'DELETE' });
};

// Story Management
export async function getAllStories(): Promise<StorySummary[]> {
  const response = await apiRequest<StorySummary[]>('/runs/summary');
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data || [];
}

export async function getSidebarStories(): Promise<SidebarStory[]> {
  const response = await apiRequest<SidebarStory[]>('/runs/summary');
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data || [];
}

export async function createStory(storyData: any): Promise<StoryResponse> {
  const response = await apiRequest<StoryResponse>('/runs', {
    method: 'POST',
    body: JSON.stringify(storyData)
  });
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data!;
}

// Character Management
export async function getUserCharacters(force: boolean = false): Promise<Character[]> {
  const response = await apiRequest<Character[]>('/characters', {}, force);
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data || [];
}

export async function deleteCharacter(characterId: number): Promise<void> {
  const response = await apiRequest<void>(`/characters/${characterId}`, { method: 'DELETE' });
  if (response.error) {
    throw new Error(response.error);
  }
}

export async function deleteStory(storyId: number): Promise<void> {
  const response = await apiRequest<void>(`/runs/${storyId}`, { method: 'DELETE' });
  if (response.error) {
    throw new Error(response.error);
  }
}

export async function getAllCharacters(force: boolean = false): Promise<StoryWithCharacters[]> {
  // Get all stories first
  const storiesResponse = await apiRequest<StorySummary[]>('/runs/summary', {}, force);
  if (storiesResponse.error) {
    throw new Error(storiesResponse.error);
  }
  
  const stories = storiesResponse.data || [];
  
  // Fetch character images in parallel for all stories
  const jobs = stories.map(async (story) => {
    try {
      const res = await apiRequest<any[]>(`/runs/${story.id}/character-images`, {}, force);
      const chars = res.data || [];
      if (!chars.length) return null;
      
      return {
        story_id: story.id,
        story_title: story.story_name || story.child_name || `Story ${story.id}`,
        created_at: story.created_at,
        characters: chars.map((c: any, idx: number) => ({
          id: c.id ?? idx, // safer fallback
          name: c.name,
          description: `${c.name} character`,
          image_url: c.image_url,
          status: c.status,
          order_index: 0,
          character_type: 'main',
          created_at: story.created_at
        }))
      };
    } catch (error) {
      console.warn(`Failed to fetch characters for story ${story.id}:`, error);
      return null;
    }
  });
  
  // Wait for all parallel requests to complete and filter out nulls
  return (await Promise.all(jobs)).filter(Boolean) as StoryWithCharacters[];
}

export async function regenerateCharacter(characterId: number): Promise<CharacterRegenerateResponse> {
  const response = await apiRequest<CharacterRegenerateResponse>(`/characters/${characterId}/regenerate`, {
    method: 'POST'
  });
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data!;
}

// File Upload
export async function getPresignedUploadUrl(filename: string, contentType: string): Promise<PresignedUploadResponse> {
  const response = await apiRequest<PresignedUploadResponse>('/upload/presigned-url', {
    method: 'POST',
    body: JSON.stringify({ filename, content_type: contentType })
  });
  
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data!;
}

export async function uploadFile(file: File): Promise<{file_url: string, key: string}> {
  const formData = new FormData();
  formData.append('file', file);
  
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}/upload/file`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  
  return await response.json();
}

// Mock API for development
const mockApi = {
  getAllStories: async (): Promise<StoryResponse[]> => {
    return [
      {
        id: 1,
        title: "The Magic Kite",
        status: "completed",
        current_step: "final",
        character_phase: "characters_complete",
        has_final_video: true,
        final_url: "https://example.com/video.mp4",
        characters: [
          {
            id: 1,
            name: "Sean",
            description: "A lonely boy with a hopeful spirit",
            image_url: "https://example.com/sean.jpg",
            status: "completed",
            order_index: 0
          }
        ],
        scenes: [],
        created_at: "2024-01-01T00:00:00Z",
        updated_at: "2024-01-01T00:00:00Z"
      }
    ];
  },
  
  createStory: async (title: string, description: string): Promise<StoryResponse> => {
    return {
      id: 2,
      title,
      status: "generating",
      current_step: "characters",
      character_phase: "fetching_characters",
      has_final_video: false,
      final_url: null,
      characters: [],
      scenes: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  },
  
  regenerateCharacter: async (characterId: number): Promise<CharacterRegenerateResponse> => {
    return {
      success: true,
      message: "Character regeneration started"
    };
  },
  
  getPresignedUploadUrl: async (filename: string, contentType: string): Promise<PresignedUploadResponse> => {
    return {
      upload_url: `https://example.com/upload/${filename}`,
      key: `uploads/${filename}`,
      file_url: `https://example.com/uploads/${filename}`
    };
  }
};

// Use mock API in development
const useMockApi = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_API === 'true';

export const api = {
  getAllStories: useMockApi ? mockApi.getAllStories : getAllStories,
  createStory: useMockApi ? mockApi.createStory : createStory,
  getAllCharacters: useMockApi ? (() => Promise.resolve([])) : getAllCharacters,
  regenerateCharacter: useMockApi ? mockApi.regenerateCharacter : regenerateCharacter,
  getPresignedUploadUrl: useMockApi ? mockApi.getPresignedUploadUrl : getPresignedUploadUrl,
  uploadFile: useMockApi ? (() => Promise.resolve({file_url: 'https://example.com/test.jpg', key: 'test.jpg'})) : uploadFile,
  deleteCharacter: useMockApi ? (() => Promise.resolve()) : deleteCharacter,
  deleteStory: useMockApi ? (() => Promise.resolve()) : deleteStory,
};

// Export individual functions for direct use
export {
  getAllStories as getAllStoriesApi,
  createStory as createStoryApi,
  getAllCharacters as getAllCharactersApi,
  getUserCharacters as getUserCharactersApi,
  regenerateCharacter as regenerateCharacterApi,
  getPresignedUploadUrl as getPresignedUploadUrlApi,
  deleteCharacter as deleteCharacterApi,
};